/**
 * Continuous ambient loops (wind, rain, storm, water, rumble, fire) whose level stages set
 * via `audio.ambient(name, level)`. Loops are built lazily, smoothed, and torn down after
 * they've been silent for a few seconds. Routed through the SFX bus.
 */
import { clamp } from './dsp';
import type { AudioGraph } from './graph';
import { noise, type Kit } from './sfx';

export const AMBIENT_NAMES = ['wind', 'rain', 'storm', 'water', 'rumble', 'fire'] as const;

interface Loop {
  out: GainNode;
  sources: AudioScheduledSourceNode[];
  /** Apply a new level (0..1) and pitch. */
  set(level: number, pitch: number, t: number): void;
  timer?: number;
  level: number;
  silentSince: number;
}

const SMOOTH = 0.25;

export class Ambience {
  private loops = new Map<string, Loop>();
  private reaper = 0;

  constructor(private readonly graph: AudioGraph) {}

  set(name: string, level: number, pitch = 1): void {
    const lv = clamp(level, 0, 1.5);
    let loop = this.loops.get(name);
    if (!loop) {
      if (lv <= 0.001) return;
      loop = this.build(name);
      if (!loop) return;
      this.loops.set(name, loop);
      this.ensureReaper();
    }
    const t = this.graph.now;
    if (Math.abs(lv - loop.level) > 0.002 || lv === 0) loop.set(lv, pitch, t);
    if (lv > 0.001) loop.silentSince = Infinity;
    else if (loop.silentSince === Infinity) loop.silentSince = t;
    loop.level = lv;
  }

  /** Fade everything out (stage change). */
  clear(): void {
    for (const name of this.loops.keys()) this.set(name, 0);
  }

  private ensureReaper(): void {
    if (this.reaper) return;
    this.reaper = window.setInterval(() => {
      const now = this.graph.now;
      for (const [name, l] of this.loops) {
        if (now - l.silentSince > 3) {
          for (const s of l.sources) s.stop();
          clearInterval(l.timer);
          l.out.disconnect();
          this.loops.delete(name);
        }
      }
      if (!this.loops.size) {
        clearInterval(this.reaper);
        this.reaper = 0;
      }
    }, 1000);
  }

  // ------------------------------------------------------------------ builders

  private src(color: 'white' | 'pink' | 'brown'): AudioBufferSourceNode {
    const s = this.graph.ctx.createBufferSource();
    s.buffer = this.graph.noiseBuffer(color);
    s.loop = true;
    s.start(this.graph.now, Math.random() * 1.5);
    return s;
  }

  private filter(type: BiquadFilterType, f: number, q = 0.7): BiquadFilterNode {
    const n = this.graph.ctx.createBiquadFilter();
    n.type = type;
    n.frequency.value = f;
    n.Q.value = q;
    return n;
  }

  private gain(v = 0): GainNode {
    const g = this.graph.ctx.createGain();
    g.gain.value = v;
    return g;
  }

  /** Slow random-ish gust modulation (two detuned LFOs) added onto `param`. */
  private gusts(param: AudioParam, depth: number, rate: number, sources: AudioScheduledSourceNode[]): GainNode {
    const ctx = this.graph.ctx;
    const d = this.gain(depth);
    for (const r of [rate, rate * 0.37]) {
      const o = ctx.createOscillator();
      o.frequency.value = r;
      o.connect(d);
      o.start();
      sources.push(o);
    }
    d.connect(param);
    return d;
  }

  private build(name: string): Loop | undefined {
    const out = this.gain(1);
    out.connect(this.graph.sfx);
    const sources: AudioScheduledSourceNode[] = [];
    const base = { out, sources, level: 0, silentSince: Infinity };
    switch (name) {
      case 'wind': {
        const s = this.src('pink');
        const bp = this.filter('bandpass', 500, 0.9);
        const vca = this.gain(0);
        const gustAmp = this.gain(1);
        s.connect(bp).connect(vca).connect(gustAmp).connect(out);
        const rush = this.src('white');
        const hp = this.filter('highpass', 2500);
        const lp = this.filter('lowpass', 7000);
        const rv = this.gain(0);
        rush.connect(hp).connect(lp).connect(rv).connect(gustAmp);
        sources.push(s, rush);
        const gd = this.gusts(gustAmp.gain, 0.25, 0.11, sources);
        const fg = this.gusts(bp.frequency, 180, 0.07, sources);
        return {
          ...base,
          set: (lv, p, t) => {
            vca.gain.setTargetAtTime(0.9 * lv, t, SMOOTH);
            rv.gain.setTargetAtTime(0.18 * lv * lv, t, SMOOTH);
            bp.frequency.setTargetAtTime((320 + 1500 * Math.pow(lv, 1.3)) * p, t, SMOOTH);
            bp.Q.setTargetAtTime(0.9 + lv * 0.6, t, SMOOTH);
            gd.gain.setTargetAtTime(0.15 + 0.2 * lv, t, SMOOTH);
            fg.gain.setTargetAtTime(120 + 400 * lv, t, SMOOTH);
          },
        };
      }
      case 'rain':
      case 'storm': {
        const storm = name === 'storm';
        const hiss = this.src('white');
        const hp = this.filter('highpass', 1400);
        const lp = this.filter('lowpass', storm ? 7500 : 9000);
        const hv = this.gain(0);
        hiss.connect(hp).connect(lp).connect(hv).connect(out);
        const body = this.src('pink');
        const blp = this.filter('lowpass', 700);
        const bv = this.gain(0);
        body.connect(blp).connect(bv).connect(out);
        sources.push(hiss, body);
        let rum: GainNode | undefined;
        let wind: GainNode | undefined;
        let wbp: BiquadFilterNode | undefined;
        if (storm) {
          const r = this.src('brown');
          const rlp = this.filter('lowpass', 140);
          rum = this.gain(0);
          r.connect(rlp).connect(rum).connect(out);
          const w = this.src('pink');
          wbp = this.filter('bandpass', 900, 1.1);
          wind = this.gain(0);
          const ga = this.gain(1);
          w.connect(wbp).connect(wind).connect(ga).connect(out);
          sources.push(r, w);
          this.gusts(ga.gain, 0.4, 0.13, sources);
          this.gusts(wbp.frequency, 400, 0.09, sources);
        }
        // Droplet patter: sparse filtered clicks scheduled while audible.
        const loop: Loop = {
          ...base,
          set: (lv, p, t) => {
            hv.gain.setTargetAtTime(0.16 * lv, t, SMOOTH);
            bv.gain.setTargetAtTime(0.35 * lv, t, SMOOTH);
            rum?.gain.setTargetAtTime(0.9 * lv, t, SMOOTH);
            wind?.gain.setTargetAtTime(0.6 * lv, t, SMOOTH);
            wbp?.frequency.setTargetAtTime(900 * p, t, SMOOTH);
          },
        };
        loop.timer = window.setInterval(() => {
          if (loop.level < 0.05 || this.graph.ctx.state !== 'running') return;
          const n = Math.round(loop.level * (storm ? 6 : 4) * Math.random());
          const k = this.kit(out);
          for (let i = 0; i < n; i++) {
            noise(k, { t0: k.t + Math.random() * 0.1, dur: 0.006, bp: 2500 + Math.random() * 4500, q: 4, vol: 0.12 * loop.level, a: 0.001, d: 0.004, s: 0.3, r: 0.01 });
          }
        }, 100);
        return loop;
      }
      case 'water': {
        const s = this.src('brown');
        const lp = this.filter('lowpass', 500, 1.2);
        const v = this.gain(0);
        s.connect(lp).connect(v).connect(out);
        sources.push(s);
        const fm = this.gusts(lp.frequency, 180, 0.09, sources);
        return {
          ...base,
          set: (lv, p, t) => {
            v.gain.setTargetAtTime(0.5 * lv, t, SMOOTH);
            lp.frequency.setTargetAtTime(500 * p, t, SMOOTH);
            fm.gain.setTargetAtTime(180 * p, t, SMOOTH);
          },
        };
      }
      case 'rumble': {
        const s = this.src('brown');
        const lp = this.filter('lowpass', 120);
        const v = this.gain(0);
        s.connect(lp).connect(v).connect(out);
        sources.push(s);
        return {
          ...base,
          set: (lv, p, t) => {
            v.gain.setTargetAtTime(1.1 * lv, t, SMOOTH);
            lp.frequency.setTargetAtTime(120 * p, t, SMOOTH);
          },
        };
      }
      case 'fire': {
        const s = this.src('pink');
        const bp = this.filter('bandpass', 900, 0.6);
        const v = this.gain(0);
        s.connect(bp).connect(v).connect(out);
        const r = this.src('brown');
        const rlp = this.filter('lowpass', 220);
        const rv = this.gain(0);
        r.connect(rlp).connect(rv).connect(out);
        sources.push(s, r);
        const loop: Loop = {
          ...base,
          set: (lv, _p, t) => {
            v.gain.setTargetAtTime(0.12 * lv, t, SMOOTH);
            rv.gain.setTargetAtTime(0.35 * lv, t, SMOOTH);
          },
        };
        loop.timer = window.setInterval(() => {
          if (loop.level < 0.05 || this.graph.ctx.state !== 'running') return;
          const k = this.kit(out);
          const n = Math.random() < loop.level * 0.9 ? 1 + Math.floor(Math.random() * 3) : 0;
          for (let i = 0; i < n; i++) {
            noise(k, { t0: k.t + Math.random() * 0.12, dur: 0.005, bp: 1500 + Math.random() * 3500, q: 3, vol: (0.2 + Math.random() * 0.3) * loop.level, a: 0.001, d: 0.004, s: 0.2, r: 0.015 });
          }
        }, 120);
        return loop;
      }
      default:
        out.disconnect();
        return undefined;
    }
  }

  private kit(out: AudioNode): Kit {
    const g = this.graph;
    return { ctx: g.ctx, out, rev: g.sfxReverbSend, t: g.now + 0.02, p: 1, v: 1, noise: (c) => g.noiseBuffer(c) };
  }
}
